import type {
  AssetTreeNodeAsset,
  AssetTreeNodesByAssetType,
  AssetTreeNodeWithGeojson,
} from "./asset-tree.type";
import type { ReferentialConditionRatingValue } from "@/entities/referential/referential-condition-ratings";
import type { GeojsonGeometryType } from "@/types";

import uniq from "lodash/uniq";

import { CONDITION_RATING_NOT_RATED } from "@/entities/referential/referential-condition-ratings";

export const getAssetTreeNodeAssetConditionRating = (
  node: AssetTreeNodeAsset
): ReferentialConditionRatingValue => {
  return node.condition_rating ?? CONDITION_RATING_NOT_RATED;
};

export const getAssetTreeNodeAssetsByType = (assets: AssetTreeNodeAsset[]) => {
  return assets.reduce<AssetTreeNodesByAssetType>((bucket, asset) => {
    const key = asset.asset_type.id;
    return {
      ...bucket,
      [key]: [...(bucket[key] ?? []), asset],
    };
  }, {});
};

/**
 * Returns an array of all the geometry types contains in asset's geojson
 *
 * @param node AssetTreeNodeLocation | AssetTreeNodeAsset
 * @returns ["Point" | "MultiPoint" | "LineString" | "MultiLineString" | "Polygon" | "MultiPolygon" | "GeometryCollection"] | undefined
 */
export const getAssetTreeNodeGeometryTypes = (
  node: AssetTreeNodeWithGeojson
): GeojsonGeometryType[] | undefined => {
  const geojson = node?.geographical_data;

  if (geojson?.type === "Feature") {
    const geoType = geojson.geometry?.type;
    return geoType ? [geoType] : undefined;
  }

  if (geojson?.type === "FeatureCollection") {
    return uniq(
      (geojson.features ?? [])
        .map((feature) => feature?.geometry?.type)
        .filter((geoType) => !!geoType)
    );
  }

  return undefined;
};

export const assetTreeNodeHasOneOfGeojsonTypes = (
  node: AssetTreeNodeWithGeojson,
  lookupTypes: GeojsonGeometryType[]
) => {
  const types = getAssetTreeNodeGeometryTypes(node);

  if (!types?.length) return false;

  for (const type of types) {
    if (lookupTypes.includes(type)) return true;
  }

  return false;
};

export const assetTreeNodeHasPerimeter = (node: AssetTreeNodeWithGeojson) => {
  return assetTreeNodeHasOneOfGeojsonTypes(node, [
    "LineString",
    "MultiLineString",
    "Polygon",
    "MultiPolygon",
  ]);
};

export const assetTreeNodeHasArea = (node: AssetTreeNodeWithGeojson) => {
  return assetTreeNodeHasOneOfGeojsonTypes(node, ["Polygon", "MultiPolygon"]);
};
